import { useEffect, useState } from 'react'
import DataGrid from '../data-grid/DataGrid.tsx'
import { useAppDispatch } from '../../reducers/store.ts'
import { useDispatch, useSelector } from 'react-redux'
import DropDown from '../dropdown/DropDown.tsx'
import { alertStatusOptions as alertStatusOptions } from '../../strings/dropdown-options.ts'
import { SelectOptionWithDarkMode } from '../../types/DropDownTypes.ts'
import { Alert, ResolveAlert } from '../../types/AlertTypes.ts'
import {
  closeAlerts,
  fetchAlerts,
  generateAndDownloadReport,
} from '../../reducers/alerts/alerts.thunk.ts'
import { selectAlerts, setAlerts } from '../../reducers/alerts/alerts.slice.ts'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../strings/api-consts.ts'
import Popout from '../popout/Popout.tsx'
import { alertColumns } from './AlertColumns.tsx'
import AlertsResolveModal from './AlertsResolveModal.tsx'
import toast from 'react-hot-toast'
import { PaginationSelectOption } from '../../types/MemberTypes.ts'
import checkIcon from '../../assets/check_circle.svg'
import { fetchThreatHunterUsers } from '../../reducers/settings/settings.thunk.ts'
import { ThreatHunterUsersResponse } from '../../types/SettingsType.ts'
import PageSkeleton from '../skeleton/PageSkeleton.tsx'
import { Pagination } from '../pagination/Pagination.tsx'
import { Button } from '@radix-ui/themes'
import AlertsResolveMultipleModal from './AlertsResolveMultipleModal.tsx'
import { getRoles, permitsRole } from '../../utils/roles.ts'
import { RoleType } from '../../types/UserTypes.ts'
import { selectUser } from '../../reducers/users/users.slice.ts'
import { inPov } from '../../utils/session.ts'
import { HEADER_HEIGHT_WITH_TRIAL, HEADER_HEIGHT_WITHOUT_TRIAL } from '../../strings/consts.ts'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts.ts'

function AlertsWrapper() {
  const di = useAppDispatch()
  const dispatch = useDispatch()
  const { alerts, totalPages, alertTableCurrentPage } = useSelector(selectAlerts)
  const { session } = useSelector(selectUser)
  const roles = getRoles(session)

  const [statusFilter, setStatusFilter] = useState<SelectOptionWithDarkMode[]>([alertStatusOptions[0]])
  const [typeFilter, _setTypeFilter] = useState<SelectOptionWithDarkMode[]>([])
  const [showPopout, setShowPopout] = useState<boolean>(false)
  const [popoutData, setPopoutData] = useState<ResolveAlert | null>(null)
  const [clickedInputs, setClickedInputs] = useState<ResolveAlert[]>([])
  const [dataLoading, setDataLoading] = useState<boolean>(true)
  const [showResolveModal, setShowResolveModal] = useState<boolean>(false)
  const [showResolveMultipleModal, setShowResolveMultipleModal] = useState<boolean>(false)
  const [threatHunterUsers, setThreatHunterUsers] = useState<ThreatHunterUsersResponse | { error: string }>({
    threatHunterUsers: [],
  })
  const defaultSortOrder = 'desc'
  const defaultSortColumn = 'updatedAt'
  const [sortOrder, setSortOrder] = useState<string>(defaultSortOrder)
  const [sortColumn, setSortColumn] = useState<string>(defaultSortColumn)
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({})

  useEffect(() => {
    setDataLoading(true)
    if (threatHunterUsers) {
      di(
        fetchAlerts(DEFAULT_PAGE, DEFAULT_PAGE_SIZE, statusFilter, typeFilter, threatHunterUsers, sortOrder, sortColumn)
      )
    }
  }, [statusFilter, typeFilter, threatHunterUsers, sortColumn, sortOrder])

  useEffect(() => {
    if (alerts || (alerts && (alerts as Alert[]).length === 0)) {
      setDataLoading(false)
    } else if (!alerts) {
      setDataLoading(true)
    }
  }, [alerts])

  useEffect(() => {
    fetchThreatHunterUsersData()
    return () => {
      dispatch(setAlerts(undefined))
    }
  }, [])

  const sortBy = (column: string) => {
    if (sortColumn === column) {
      if (sortOrder === 'desc' && column !== defaultSortColumn) {
        // Clear Sort
        setSortColumn(defaultSortColumn)
        setSortOrder(defaultSortOrder)
      } else {
        // Sort Existing Column
        setSortOrder(sortOrder == 'desc' ? 'asc' : 'desc')
      }
    } else {
      // Sort New Column
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const fetchThreatHunterUsersData = async () => {
    const users = await di(fetchThreatHunterUsers())
    setThreatHunterUsers(users)
  }

  const handleCloseAlerts = async (alerts: ResolveAlert[]) => {
    const closed = await di(closeAlerts(alerts))
    if (closed) {
      toast.success(`${alerts.length} alert${alerts.length === 1 ? ' was' : 's were'} successfully resolved`, DEFAULT_TOAST_CONFIG)
      setCheckedItems({})
      setClickedInputs([])
      await di(
        fetchAlerts(DEFAULT_PAGE, DEFAULT_PAGE_SIZE, statusFilter, typeFilter, threatHunterUsers, sortOrder, sortColumn)
      )
      setShowPopout(false)
    } else {
      toast.error('There was an issue resolving the alerts', DEFAULT_TOAST_CONFIG)
    }
  }

  const downloadAlertReport = async (scanId: string, isThreatHunter: boolean, isDemo?: boolean) => {
    const isDemoDownload = isDemo || session?.user?.email === 'demo@acme-inc.io'
    await di(generateAndDownloadReport(scanId, isThreatHunter, isDemoDownload))
  }

  const handlePageClick = (data: PaginationSelectOption) => {
    setDataLoading(true)
    di(
      fetchAlerts(
        data.selected + 1,
        DEFAULT_PAGE_SIZE,
        statusFilter,
        typeFilter,
        threatHunterUsers,
        sortOrder,
        sortColumn
      )
    )
  }

  const handleCheckboxChange = (id: number) => {
    const selectedItem = alerts!.find((item) => item.id === id)
    const mappedSelectedItem = {
      alertId: selectedItem!.id,
      scanId: selectedItem!.scanId,
      alertName: selectedItem!.alertName,
      alertType: selectedItem!.alertType,
    }
    if (clickedInputs.some(x => x.alertId === id)) {
      setClickedInputs(clickedInputs.filter((item) => item.alertId !== id))
    } else {
      setClickedInputs([...clickedInputs, mappedSelectedItem])
    }
  }

  return (
    <>
      <div
        className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l bg-contentBackground w-[calc(100%_-_208px)] p-9"
        style={{
          height: inPov(session)
            ? `calc(100% - ${HEADER_HEIGHT_WITH_TRIAL})`
            : `calc(100% - ${HEADER_HEIGHT_WITHOUT_TRIAL})`,
        }}
      >
        {!dataLoading && (
          <div className="mb-6 flex w-full">
            <DropDown
              value={statusFilter}
              onChangeHandler={setStatusFilter}
              options={alertStatusOptions}
              style="z-10 h-8 mr-4 min-w-[160px]"
              placeholder="Alert Status"
              multiple={true}
              disabled={false}
            />
            {/* Commented out until sensitive travel is released */}
            {/* <DropDown
              value={typeFilter}
              onChangeHandler={setTypeFilter}
              options={alertTypeOptions}
              style="z-10 h-8 mr-4 min-w-[160px]"
              placeholder="Alert Type"
              multiple={true}
              disabled={false}
            /> */}
            <div className="ml-auto flex">
              {permitsRole(roles, [RoleType.ACCESS_MGMT]) && (
                <Button
                  onClick={() => setShowResolveMultipleModal(true)}
                  style={{ height: '38px', marginLeft: '8px' }}
                  variant="outline"
                  className={`outline-1 ${clickedInputs && clickedInputs.length <= 0 ? 'dark:text-neutral-11  dark:outline-neutral-11' : 'dark:border-secondaryPurple dark:text-secondaryPurple'} dark:shadow-none dark:outline`}
                  disabled={clickedInputs && clickedInputs.length <= 0}
                >
                  Resolve Alerts
                </Button>
              )}
            </div>
          </div>
        )}

        {!alerts && (
          <div className="flex justify-center items-center min-h-screen">
            <PageSkeleton />
          </div>
        )}
        {alerts && alerts.length > 0 && (
          <>
            <DataGrid
              columns={alertColumns(
                checkedItems,
                setCheckedItems,
                handleCheckboxChange,
                setShowPopout,
                setPopoutData,
                downloadAlertReport
              )}
              sortFunction={(column: string) => {
                sortBy(column)
              }}
              sortColumn={sortColumn}
              sortOrder={sortOrder}
              data={alerts}
              loading={dataLoading}
              classNames={inPov(session) ? 'max-h-[calc(100vh_-_276px)]' : 'max-h-[calc(100vh_-_240px)]'}
            />
            <div className="mt-2">
              <Pagination totalPages={totalPages} forcePage={alertTableCurrentPage} handlePageClick={handlePageClick} />
            </div>
          </>
        )}
        {alerts && alerts.length === 0 && (
          <div className="block items-center mt-20">
            <div className="flex items-center justify-center">
              <img src={checkIcon} alt="No open alerts found" className="h-12 w-12" />
            </div>{' '}
            <p className="text-center font-bold">No Open Alerts</p>
            <p className="text-center text-sm">You have no actions for now</p>
          </div>
        )}
      </div>
      {showPopout && (
        <Popout
          setShowPopout={setShowPopout}
          contentScreen="ALERT"
          popoutData={popoutData!.alertId}
          setShowResolveModal={setShowResolveModal}
        />
      )}
      {showResolveModal && (
        <AlertsResolveModal
          setShowResolveModal={setShowResolveModal}
          resolveAlerts={handleCloseAlerts}
          alerts={[popoutData!]}
        />
      )}
      {showResolveMultipleModal && (
        <AlertsResolveMultipleModal
          setShowResolveModal={setShowResolveMultipleModal}
          resolveAlerts={handleCloseAlerts}
          alerts={clickedInputs.map((alert) => alert)}
        />
      )}
    </>
  )
}

export default AlertsWrapper
